import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Form, InputNumber, Space, Typography, message } from 'antd';

import {
  getDashboardDefaults,
  updateDashboardDefaults,
} from '../../services/dashboardSettingsService';
import { DashboardDefaultsDto } from '../../types/DashboardSettings';
import { SkeletonBlock, SkeletonRegion } from '../common/Skeleton/Skeleton';

const formatDate = (value?: string | null): string => {
  if (!value) return '-';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

interface DashboardDefaultsPanelProps {
  highlightKey?: string | null;
}

const DashboardDefaultsPanel = ({ highlightKey }: DashboardDefaultsPanelProps) => {
  const [form] = Form.useForm<{ lowStorageTriggerPercent: number }>();
  const [defaults, setDefaults] = useState<DashboardDefaultsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [highlightStorageAttention, setHighlightStorageAttention] = useState(false);

  const currentThreshold = defaults?.lowStorageTriggerPercent;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getDashboardDefaults()
      .then((response) => {
        if (cancelled) return;
        setDefaults(response);
        form.setFieldsValue({ lowStorageTriggerPercent: response.lowStorageTriggerPercent });
        setDirty(false);
      })
      .catch((loadError) => {
        if (cancelled) return;
        setError(loadError?.response?.data?.message || loadError?.message || 'Storage Attention settings could not be loaded.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [form]);

  const helperText = useMemo(
    () =>
      currentThreshold == null
        ? ''
        : `Storage Attention shows RTUs with less than ${Math.max(0, 100 - currentThreshold)}% free disk space. Current threshold: disk usage at or above ${currentThreshold}%.`,
    [currentThreshold]
  );

  useEffect(() => {
    if (highlightKey !== 'storage-attention') {
      setHighlightStorageAttention(false);
      return undefined;
    }

    setHighlightStorageAttention(true);
    const timeout = window.setTimeout(() => {
      setHighlightStorageAttention(false);
    }, 3000);

    return () => window.clearTimeout(timeout);
  }, [highlightKey]);

  const save = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const updated = await updateDashboardDefaults({
        lowStorageTriggerPercent: values.lowStorageTriggerPercent,
      });
      setDefaults(updated);
      form.setFieldsValue({ lowStorageTriggerPercent: updated.lowStorageTriggerPercent });
      setDirty(false);
      void message.success('Storage Attention settings saved');
    } catch (saveError: any) {
      void message.error(saveError?.response?.data?.message || saveError?.message || 'Storage Attention settings could not be saved');
    } finally {
      setSaving(false);
    }
  };

  const showSkeleton = loading && !defaults;

  return (
    <div className="admin-settings-dashboard-defaults">
      <Card
        size="small"
        title="Storage Attention"
        className={highlightStorageAttention ? 'admin-settings-dashboard-defaults__card--highlighted' : undefined}
      >
        <Space direction="vertical" size={14} className="admin-settings-dashboard-defaults__stack">
          {error ? <Alert type="warning" showIcon message={error} /> : null}
          {showSkeleton ? (
            <SkeletonRegion label="Loading storage attention settings">
              <SkeletonBlock width="80%" height="0.9rem" />
              <SkeletonBlock width="12rem" height="0.75rem" />
              <SkeletonBlock width="8rem" height="2rem" />
              <SkeletonBlock width="5rem" height="2rem" />
            </SkeletonRegion>
          ) : defaults ? (
            <>
              <Typography.Text>{helperText}</Typography.Text>
              <Form
                form={form}
                layout="vertical"
                className="admin-settings-dashboard-defaults__form"
                onValuesChange={() => setDirty(true)}
              >
                <Form.Item
                  name="lowStorageTriggerPercent"
                  label="Low storage trigger (%)"
                  rules={[
                    { required: true, message: 'Low storage trigger is required' },
                    { type: 'number', min: 1, max: 100, message: 'Value must be between 1 and 100' },
                  ]}
                >
                  <InputNumber min={1} max={100} addonAfter="%" disabled={loading} aria-label="Low storage trigger (%)" />
                </Form.Item>
                <Space align="center">
                  <Button type="primary" loading={saving} disabled={!dirty || loading} onClick={save}>
                    Save
                  </Button>
                  <span className="admin-settings-dashboard-defaults__meta">
                    Updated: {formatDate(defaults.updatedAtUtc)}
                    {defaults.updatedByName ? ` by ${defaults.updatedByName}` : ''}
                  </span>
                </Space>
              </Form>
            </>
          ) : null}
        </Space>
      </Card>
    </div>
  );
};

export default DashboardDefaultsPanel;
